import {
  countryName,
  formatWhole,
  getFootprint,
  getPlan,
  NotFound,
  type Footprint,
  type FootprintLine,
  type Mode,
  type Plan,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useLive } from '../../app/live';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan };

/** Each kind's colour, as the map draws it. */
const COLOURS: Record<Mode | 'stay' | 'towns', string> = {
  flight: 'var(--way-flight)',
  train: 'var(--way-train)',
  bus: 'var(--way-bus)',
  ferry: 'var(--way-ferry)',
  car: 'var(--way-car)',
  foot: 'var(--way-walk)',
  stay: 'var(--line-strong)',
  towns: 'var(--way-bus)',
};

/** The colour of a line. */
function colourOf(line: FootprintLine): string {
  return COLOURS[line.kind === 'way' ? line.mode : line.kind];
}

/** Each kind counted in, by the colour of its ways. */
const GROUPS = {
  flights: COLOURS.flight,
  trains: COLOURS.train,
  ground: COLOURS.bus,
  stays: COLOURS.stay,
} as const;

/** The kind a line is counted in: flights, trains, the rest on the ground, or stays. */
function groupOf(line: FootprintLine): 'flights' | 'trains' | 'ground' | 'stays' {
  if (line.kind === 'stay') return 'stays';
  if (line.kind === 'way' && line.mode === 'flight') return 'flights';
  if (line.kind === 'way' && line.mode === 'train') return 'trains';
  return 'ground';
}

/** How much longer, said: `+3 h`, `+2 days`. */
function longerSaid(minutes: number, language: string): string {
  const format = new Intl.NumberFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    style: 'unit',
    unit: minutes >= 1440 ? 'day' : 'hour',
    unitDisplay: 'long',
    maximumFractionDigits: 0,
  });
  return format.format(
    minutes >= 1440 ? Math.round(minutes / 1440) : Math.max(1, Math.round(minutes / 60)),
  );
}

/**
 * A trip's carbon footprint (the Emissions mockup), beside Countries and Routes: one traveller's
 * CO₂e, as its money is counted — the trip in all, by kind, each way and stay, and the flights
 * that could go on the ground.
 */
export function Emissions() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { id = '' } = useParams();
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [rf, setRf] = useState(true);
  const [print, setPrint] = useState<Footprint | null>(null);

  useEffect(() => {
    let live = true;
    getPlan(id).then(
      (plan) => {
        if (live) setState({ kind: 'ready', plan });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, changed]);

  useEffect(() => {
    let live = true;
    getFootprint(id, rf).then(
      (read) => {
        if (live) setPrint(read);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [id, rf, changed]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { plan } = state;
  const number = (value: number) => Math.round(value).toLocaleString(lang);
  const groups = (['flights', 'trains', 'ground', 'stays'] as const).map((group) => {
    const lines = print?.lines.filter((line) => groupOf(line) === group) ?? [];
    const nights = lines.reduce(
      (sum, line) => sum + (line.kind === 'stay' ? line.nights : 0),
      0,
    );
    return {
      group,
      kg: lines.reduce((sum, line) => sum + line.kg, 0),
      count: group === 'stays' ? nights : lines.length,
      colour: GROUPS[group],
    };
  });
  const largest = Math.max(1, ...(print?.lines.map((line) => line.kg) ?? []));
  const lineName = (line: FootprintLine) => {
    if (line.kind === 'stay') {
      return t('emissions.stay', {
        country: countryName(line.country, lang),
        count: line.nights,
      });
    }
    if (line.kind === 'towns') return t('emissions.towns', { count: line.rides });
    return line.name;
  };
  const lineDetail = (line: FootprintLine) => {
    if (line.kind === 'stay') {
      return t('emissions.stayHow', {
        nights: line.nights,
        per: line.perNight.toLocaleString(lang),
        sharing: line.sharing,
      });
    }
    if (line.kind === 'way' && line.via.length > 0) {
      return t('emissions.via', { cities: line.via.join(', ') });
    }
    return null;
  };
  const lineKind = (line: FootprintLine) => {
    if (line.kind === 'stay') return t('emissions.hotel');
    if (line.kind === 'towns') return t('emissions.townsKind');
    return line.cabin === null
      ? t(`mode.${line.mode}`)
      : `${t(`mode.${line.mode}`)} · ${t(`emissions.cabin.${line.cabin}`)}`;
  };

  return (
    <div className="page emissions-page">
      <header className="page-head">
        <h1>{t('emissions.title')}</h1>
        <p className="lead">{t('emissions.lead')}</p>
      </header>
      {print === null ? (
        <p className="quiet">{t('emissions.loading')}</p>
      ) : (
        <>
          <div className="emissions-layout">
            <section className="panel emissions-total" aria-labelledby="emissions-total">
              <h2 id="emissions-total" className="panel-title">
                {t('emissions.oneTraveller', { name: plan.trip.name })}
              </h2>
              <p className="emissions-big">
                {number(print.kg)} <small>{t('emissions.unit')}</small>
              </p>
              <p className="quiet">
                {t('emissions.perDay', {
                  kg: number(print.kg / Math.max(1, print.days)),
                  km: number(print.km),
                })}
              </p>
              <div className="emissions-split" role="img" aria-label={t('emissions.split')}>
                {groups.map((one) => (
                  <i
                    key={one.group}
                    style={{
                      width: `${String((one.kg / Math.max(1, print.kg)) * 100)}%`,
                      background: one.colour,
                    }}
                  />
                ))}
              </div>
              <ul className="emissions-legend">
                {groups.map((one) => (
                  <li key={one.group}>
                    <span className="emissions-swatch" style={{ background: one.colour }} />
                    <span>
                      {one.group === 'ground'
                        ? t('emissions.ground')
                        : t(`emissions.${one.group}`, { count: one.count })}
                    </span>
                    <b>{number(one.kg)} kg</b>
                  </li>
                ))}
              </ul>
              <label className="check">
                <input
                  type="checkbox"
                  checked={rf}
                  onChange={(event) => {
                    setRf(event.target.checked);
                  }}
                />
                {t('emissions.rf')}
              </label>
              <p className="quiet emissions-small">{t('emissions.basis')}</p>
            </section>
            <section className="panel emissions-lines" aria-labelledby="emissions-lines">
              <div className="emissions-head">
                <h2 id="emissions-lines" className="panel-title">
                  {t('emissions.ways')}
                </h2>
                <span className="quiet emissions-small">{t('emissions.largest')}</span>
              </div>
              <div className="table-scroll">
                <table className="emissions-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('emissions.way')}</th>
                      <th scope="col">{t('emissions.kind')}</th>
                      <th scope="col" className="emissions-number">
                        {t('emissions.km')}
                      </th>
                      <th scope="col" className="emissions-number">
                        {t('emissions.kg')}
                      </th>
                      <th scope="col">
                        <span className="visually-hidden">{t('emissions.split')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {print.lines.map((line) => {
                      const detail = lineDetail(line);
                      return (
                        <tr key={`${line.kind} ${line.name}`}>
                          <td>
                            {lineName(line)}
                            {detail !== null && <small>{detail}</small>}
                            {line.kind === 'stay' && line.estimated && (
                              <span className="tag">{t('emissions.estimated')}</span>
                            )}
                          </td>
                          <td>
                            <span className="emissions-mode">
                              <i style={{ background: colourOf(line) }} />
                              {lineKind(line)}
                            </span>
                          </td>
                          <td className="emissions-number">
                            {line.kind === 'stay' ? '—' : number(line.km)}
                          </td>
                          <td className="emissions-number">{number(line.kg)}</td>
                          <td className="emissions-bar">
                            <span
                              style={{
                                width: `${String((line.kg / largest) * 100)}%`,
                                background: colourOf(line),
                              }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="panel emissions-swaps" aria-labelledby="emissions-swaps">
              <h2 id="emissions-swaps" className="panel-title">
                {t('emissions.swaps')}
              </h2>
              {print.swaps.length === 0 && <p className="quiet">{t('emissions.noSwaps')}</p>}
              {print.swaps.map((swap) => {
                const amount = formatWhole(
                  { minor: Math.abs(swap.dearer), currency: plan.trip.currency },
                  lang,
                );
                return (
                  <div className="emissions-swap" key={`${String(swap.at)} ${swap.mode}`}>
                    <b>
                      {t('emissions.swap', {
                        from: swap.from,
                        to: swap.to,
                        mode: t(`mode.${swap.mode}`).toLowerCase(),
                      })}
                    </b>
                    <span className="emissions-delta">
                      <span className="good">
                        {t('emissions.saves', { kg: number(swap.saves) })}
                      </span>
                      <span className="bad">
                        {t('emissions.longer', { time: longerSaid(swap.longer, lang) })}
                      </span>
                      <span className={swap.dearer > 0 ? 'bad' : 'good'}>
                        {t(swap.dearer > 0 ? 'emissions.dearer' : 'emissions.cheaper', {
                          amount,
                        })}
                      </span>
                    </span>
                    <Link
                      className="button button-small"
                      to={`/trips/${id}/routes?stop=${swap.stop}`}
                    >
                      {t('emissions.openRoutes')}
                    </Link>
                  </div>
                );
              })}
              <p className="quiet emissions-small">{t('emissions.routesNote')}</p>
            </section>
          </div>
          <section className="panel emissions-data" aria-labelledby="emissions-data">
            <h2 id="emissions-data" className="panel-title">
              {t('emissions.data')}
            </h2>
            <ol>
              {(['note1', 'note2', 'note3', 'note4', 'note5'] as const).map((note) => (
                <li key={note}>{t(`emissions.${note}`)}</li>
              ))}
            </ol>
          </section>
        </>
      )}
    </div>
  );
}
